import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider, Form, Modal, notification, Typography } from 'antd';

import {
  CompanyConsumableStockPricesRequest,
  ConsumableStockPricesResult,
} from '@/core/domain/stock/StockItems/StockItems.types';

import CompanyConsumableStockPricesForm, {
  CompanyConsumableStockPricesFormData,
} from '../CompanyConsumableStockPricesForm';
import { useDefaultFormErrorNotification, useRouteMatchParam } from '@/hooks';
import { parseCurrency } from '@/utils/number';
import { CostItemsFieldsOptions } from '@/core/domain/stock/Configuration/Configuration.types';
import { useCompanyConsumableStockPrices } from '@/core/domain/stock/StockItems/StockItems.hooks';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { useCompanyStockConfiguration } from '@/core/domain/stock/Configuration/Configuration.hooks';

const { Paragraph } = Typography;

interface CompanyConsumableStockPricesModalProps {
  visible: boolean;
  handleVisible: (prices?: ConsumableStockPricesResult) => void;
  consumableStockPrices?: ConsumableStockPricesResult;
}

function CompanyConsumableStockPricesModal({
  visible,
  handleVisible,
  consumableStockPrices,
}: CompanyConsumableStockPricesModalProps) {
  const intl = useIntl();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const [form] = Form.useForm<CompanyConsumableStockPricesFormData>();

  const consumableId = +useRouteMatchParam('consumableId');

  const companyStockConfiguration = useCompanyStockConfiguration(
    visible ? consumableStockPrices?.company.id : undefined,
  );

  const mutation = useCompanyConsumableStockPrices();

  useEffect(() => {
    const stockConfig = companyStockConfiguration.data;

    if (consumableStockPrices && stockConfig?.result) {
      form.setFieldsValue({
        ...consumableStockPrices,
        ipi: consumableStockPrices.ipi * 100,
        fixedCost: (stockConfig.result.fixedCost || 0) * 100,
        operationalExpenses: consumableStockPrices.operationalExpenses * 100,
      });
    }
  }, [consumableStockPrices, companyStockConfiguration.dataUpdatedAt]);

  const handleOk = async () => {
    try {
      if (!consumableStockPrices) return;

      const values = await form.validateFields();

      const req: CompanyConsumableStockPricesRequest = {
        consumableId,
        companyId: consumableStockPrices?.company.id,
        minStock: values.minStock,
        maxStock: values.maxStock,
        buyPrice: parseCurrency(values.buyPrice),
        ipi: values.ipi ? values.ipi / 100 : 0,
        taxReplacement: parseCurrency(values.taxReplacement),
        freightCost: parseCurrency(values.freightCost),
        otherExpenses: parseCurrency(values.otherExpenses),
        operationalExpenses: values.operationalExpenses / 100,
        location: values.location,
      };

      await mutation.mutateAsync(req);

      notification.success({
        message: intl.formatMessage({
          id: 'consumables.stock.update.notifications.success.title',
        }),
        description: intl.formatMessage({
          id: 'consumables.stock.update.notifications.success.description',
        }),
      });

      handleVisible();
    } catch (err) {
      sendFormErrorNotification(err);
    }
  };

  const handleCancel = () => {
    handleVisible();
  };

  return (
    <Modal
      title={`${intl.formatMessage({ id: 'consumables.stock' })}: ${
        consumableStockPrices?.company.name
      }`}
      open={visible}
      onOk={handleOk}
      confirmLoading={mutation.isLoading}
      onCancel={handleCancel}
      width={765}
    >
      {companyStockConfiguration.isLoading && (
        <ModalLoadingOverlay posAbsolute />
      )}

      <CompanyConsumableStockPricesForm
        form={form}
        costFields={consumableStockPrices?.costFields}
        key={companyStockConfiguration.dataUpdatedAt}
      />

      <Divider />

      <Paragraph>
        <FormattedMessage
          id="consumables.stock.enabledCostFields"
          values={{ company: consumableStockPrices?.company.name }}
        />
      </Paragraph>
      <Paragraph>
        <ul>
          {consumableStockPrices?.costFields
            .map((a) =>
              a.active ? (
                <li key={a.field}>
                  <FormattedMessage
                    id={`companies.settings.priceComposition.domain.enums.item_field_calculate.${
                      CostItemsFieldsOptions[a.field]
                    }`}
                  />
                </li>
              ) : null,
            )
            .filter((a) => a)}
        </ul>
      </Paragraph>
    </Modal>
  );
}

export default CompanyConsumableStockPricesModal;
